import { default as React, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalProps,
  StatusBar,
  Share,
  Platform,
  ActivityIndicator
} from 'react-native';
import styled from 'styled-components/native';
import { Feather, MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { formatStudyLength } from '../../common/formatStudyLength';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import AlertBox from '../../components/AlertBox';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Spacer from '../../components/Spacer';
import { H1, H3, H4, P, Subtitle } from '../../components/Typography';
import { useUserContext } from '../../context/UserContext';
import { useGetStudy } from '../../hooks/study/useGetStudy';
import useAddStudyToFavorite from '../../hooks/user-favorite/useAddStudyToFavorite';
import useDeleteStudyToFavorite from '../../hooks/user-favorite/useDeleteStudyToFavorite';
import useGetStudyIsFavorite from '../../hooks/user-favorite/useGetStudyIsFavorite';
import HtmlWebView from '../../components/HtmlWebView';
import Colors from '../../constants/Colors';
import { useGetStudyIntake } from '../../hooks/study/useGetStudyIntake';
import { useJoinPublicStudy } from '../../hooks/study/useJoinPublicStudy';
import { useHasActiveStudy } from '../../hooks/study/useHasActiveStudy';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';
import { useGetStudyQualification } from '../../hooks/study/useGetStudyQualification';
import elementIDs from '../../../elementIDs';

const StyledContainer = styled.ScrollView<{ backgroundColor?: string }>`
  position: relative;
  flex: 1;
  background: ${p => (p.backgroundColor ? p.backgroundColor : '#070c3c')};
`;

const StyledBackgroundImage = styled.Image`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 424px;
  background-color: white;
`;

const StyledHeader = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 64px 12px 0 12px;
  width: 100%;
`;

const StyledStudyNameView = styled.View`
  padding: 96px 24px 24px 24px; //125px 36px 40px 36px;
`;

const StyledCardView = styled.View`
  padding: 34px 16px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  background: #fdfbf7;
  height: 100%;
`;

const StyledHeaderTitleView = styled.View`
  flex: 1;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: 8px;
`;

const StyledHeaderFunctionView = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  width: 70px;
`;

const StyledBackButton = styled(IconButton)`
  width: 70px;
  align-items: flex-start;
`;

const StyledH1 = styled(H1)<{ textColor?: string }>`
  ${p => (p.textColor ? `color: ${p.textColor};` : '')}
`;

const StyledSubtitle = styled(Subtitle)<{ textColor?: string }>`
  ${p => (p.textColor ? `color: ${p.textColor};` : '')}
`;

export interface StudyIntroModalProps {
  onClose?: () => void;
  onFavorite?: () => void;
  onShare?: () => void;
  studyId: number;
  title?: string;
}

const StudyIntroModal: React.FC<ModalProps & StudyIntroModalProps> = ({
  onClose,
  onShare,
  onFavorite,
  studyId,
  title,
  ...props
}) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { accessToken, user, pastOccurrences, futureOccurrences } =
    useUserContext();
  const {
    addStudyToFavoriteData,
    addStudyToFavorite,
    clearAddStudyToFavoriteData
  } = useAddStudyToFavorite(user?.id || -1, studyId);
  const { study, loadingStudyError, loadingStudy, getStudy } =
    useGetStudy(studyId);
  const { loadingStudyIntake, loadingStudyIntakeError, getStudyIntake } =
    useGetStudyIntake();
  const {
    loading: joiningPublicStudy,
    error: joiningPublicStudyError,
    joinPublicStudy
  } = useJoinPublicStudy();
  const { studyIsFavorite: studyFavorite, getStudyFavorite } =
    useGetStudyIsFavorite(user?.id || -1, studyId);
  const {
    deleteStudyToFavoriteData,
    deleteStudyToFavorite,
    clearDeleteStudyToFavoriteData
  } = useDeleteStudyToFavorite(user?.id, studyId);
  const {
    loadingStudyQualification,
    loadingStudyQualificationError,
    getStudyQualification
  } = useGetStudyQualification();

  useEffect(() => getStudy(), [getStudy]);
  useEffect(() => getStudyFavorite(), [getStudyFavorite]);
  useEffect(() => {
    if (addStudyToFavoriteData) {
      onFavorite?.();
      clearAddStudyToFavoriteData();
      getStudyFavorite();
    }
  }, [
    addStudyToFavoriteData,
    clearAddStudyToFavoriteData,
    getStudyFavorite,
    onFavorite
  ]);

  useEffect(() => {
    if (deleteStudyToFavoriteData) {
      onFavorite?.();
      clearDeleteStudyToFavoriteData();
      getStudyFavorite();
    }
    // show light status var
    StatusBar.setBarStyle('light-content');
    return () => {
      StatusBar.setBarStyle('dark-content');
    };
  }, [
    clearDeleteStudyToFavoriteData,
    deleteStudyToFavoriteData,
    getStudyFavorite,
    onFavorite
  ]);
  const isFavorite = useMemo(() => studyFavorite, [studyFavorite]);
  const hasActiveStudy = useHasActiveStudy();

  const { studyParticipants } = useUserContext();
  const alreadyJoined = useMemo(() => {
    return studyParticipants?.some(sp => sp.studyId === study?.id);
  }, [studyParticipants, study?.id]);

  return (
    <Modal animationType='slide' {...props}>
      <StyledContainer
        contentContainerStyle={{ flexGrow: 1 }}
        backgroundColor={
          study?.backgroundImageUrl ? undefined : study?.backgroundColor
        }
      >
        {study?.backgroundImageUrl && (
          <StyledBackgroundImage
            resizeMode='cover'
            source={{
              uri: `${study.backgroundImageUrl}&token=${accessToken}`
            }}
          />
        )}
        <StyledHeader>
          <StyledBackButton
            testID={elementIDs.studyIntroBackButtonID}
            onPress={() => onClose?.()}
          >
            <Feather
              name='chevron-left'
              size={32}
              color={study?.foregroundColor || 'white'}
            />
          </StyledBackButton>
          <StyledHeaderTitleView>
            <StyledSubtitle
              testID={elementIDs.studyIntroHeaderTitleID}
              color='light'
              textColor={study?.foregroundColor}
            >
              {title || study?.name || ''}
            </StyledSubtitle>
          </StyledHeaderTitleView>
          <StyledHeaderFunctionView>
            <IconButton
              testID={elementIDs.studyIntroFavoriteIconID}
              onPress={() => {
                if (user?.id == null) {
                  return;
                }
                if (isFavorite) deleteStudyToFavorite();
                else addStudyToFavorite();
              }}
            >
              <MaterialIcons
                name={isFavorite ? 'favorite' : 'favorite-outline'}
                color={study?.foregroundColor || 'white'}
                size={24}
              />
            </IconButton>
            {Platform.OS != 'web' && study?.shareUrl && (
              <StyledIconButton
                testID={elementIDs.studyIntroShareIconID}
                onPress={async () => {
                  await Share.share({
                    message: study?.shareUrl || ''
                  })
                    .catch(error => {
                      log('onShare error', stringify(error));
                    })
                    .then(() => {
                      onShare?.();
                    });
                }}
              >
                <Feather
                  name='share'
                  size={24}
                  color={study?.foregroundColor || 'white'}
                />
              </StyledIconButton>
            )}
          </StyledHeaderFunctionView>
        </StyledHeader>
        <StyledStudyNameView>
          <StyledH1
            testID={elementIDs.studyIntroTitleID}
            color='light'
            textColor={study?.foregroundColor}
          >
            {study?.name || 'Study'}
          </StyledH1>
        </StyledStudyNameView>
        <StyledCardView>
          {loadingStudyError && (
            <AlertBox severity='error'>{loadingStudyError.message}</AlertBox>
          )}
          <H3 testID={elementIDs.studyIntroDurationID} color='dark'>
            {study && study.studyLength
              ? `${formatStudyLength(study.studyLength, true)
                  .split(' ')
                  .map(e => e.charAt(0).toUpperCase() + e.slice(1))
                  .join(' ')} ${'Study'}`
              : ''}
          </H3>
          <Spacer size={1} />
          <P testID={elementIDs.studyIntroOrganizationID} color='dark'>
            {study?.organization?.name || 'Community'}
          </P>
          <Spacer size={3} />
          {study?.introduction ? (
            <>
              <H4 color='dark'>Overview</H4>
              <Spacer size={2} />
              <HtmlWebView
                bodyColor={Colors.darkText}
                html={study.introduction}
              />
            </>
          ) : (
            <></>
          )}
          <Spacer size={4} />
          {hasActiveStudy && (
            <>
              <AlertBox severity='error'>
                You are already enrolled in a study. Please complete the study
                first before starting another study.
              </AlertBox>
              <Spacer size={2} />
            </>
          )}
          <Button
            testID={elementIDs.studyIntroStudySignUpButtonID}
            disabled={
              alreadyJoined ||
              !study?.intakeFormTemplateId ||
              (Boolean(study?.organization) && !study.isPublic) ||
              ((pastOccurrences.length !== 0 ||
                futureOccurrences.length !== 0) &&
                hasActiveStudy)
            }
            endIcon={
              (loadingStudyIntake ||
                joiningPublicStudy ||
                loadingStudyQualification) && <ActivityIndicator size='small' />
            }
            onPress={async () => {
              if (study?.isPublic) {
                // join a public sponsored study
                const studyParticipant = await joinPublicStudy(study.id);
                if (!studyParticipant) return;
                if (study.qualificationFormTemplateId) {
                  const studyQualification = await getStudyQualification(
                    studyParticipant.id
                  );
                  onClose?.();
                  navigation.reset({
                    index: 0,
                    routes: [
                      {
                        name: 'StudiesNav',
                        state: {
                          index: 1,
                          routes: [
                            { name: 'MyStudies' },
                            {
                              name: 'QualificationForm',
                              params: {
                                formTemplateId:
                                  study.qualificationFormTemplateId,
                                studyParticipantId: studyParticipant.id,
                                fromJoinScreen: true,
                                entityDataId: studyQualification?.id
                              }
                            }
                          ]
                        }
                      }
                    ]
                  });
                } else {
                  const studyIntake = await getStudyIntake(studyParticipant.id);
                  if (!studyIntake) return;
                  onClose?.();
                  navigation.navigate('StudiesNav', {
                    screen: 'FormTemplate',
                    params: {
                      formTemplateId: studyIntake.formTemplateId,
                      entityDataId: studyIntake.id,
                      studyParticipantId: studyParticipant.id
                    }
                  });
                }
              } else if (study?.intakeFormTemplateId) {
                // start a new self study
                onClose?.();
                navigation.navigate('StudiesNav', {
                  screen: 'FormTemplate',
                  params: {
                    formTemplateId: study?.intakeFormTemplateId
                  }
                });
              }
            }}
          >
            Sign Up For Study
          </Button>
          {loadingStudyIntakeError && (
            <>
              <Spacer size={2} />
              <AlertBox severity='error'>
                {loadingStudyIntakeError.message}
              </AlertBox>
            </>
          )}
          {joiningPublicStudyError && (
            <>
              <Spacer size={2} />
              <AlertBox severity='error'>
                {joiningPublicStudyError.message}
              </AlertBox>
            </>
          )}
          {loadingStudyQualificationError && (
            <>
              <Spacer size={2} />
              <AlertBox severity='error'>
                {loadingStudyQualificationError.message}
              </AlertBox>
            </>
          )}
        </StyledCardView>
        {loadingStudy && <ActivityIndicatorScreen modal />}
      </StyledContainer>
    </Modal>
  );
};

export default StudyIntroModal;
